import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateSalt, deriveKey, bufferToBase64, base64ToBuffer } from '../utils/crypto';
import { initDB, saveEncrypted, getEncrypted, STORES } from '../utils/storage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPasscode, setHasPasscode] = useState(false);

  useEffect(() => {
    checkExistingPasscode();
  }, []);

  const checkExistingPasscode = async () => {
    try {
      await initDB();
      const db = await indexedDB.open('AravelaDB', 1);
      
      db.onsuccess = () => {
        const transaction = db.result.transaction([STORES.AUTH], 'readonly');
        const store = transaction.objectStore(STORES.AUTH);
        const request = store.get('passcode_data');

        request.onsuccess = () => {
          setHasPasscode(!!request.result);
          setIsLoading(false);
        };

        request.onerror = () => {
          setIsLoading(false);
        };
      };
    } catch (error) {
      console.error('Error checking passcode:', error);
      setIsLoading(false);
    }
  };

  const createPasscode = async (passcode) => {
    try {
      const salt = generateSalt();
      const key = await deriveKey(passcode, salt);

      // Store salt (not the passcode or key)
      const db = await indexedDB.open('AravelaDB', 1);
      
      return new Promise((resolve, reject) => {
        db.onsuccess = () => {
          const transaction = db.result.transaction([STORES.AUTH], 'readwrite');
          const store = transaction.objectStore(STORES.AUTH);
          const request = store.put({
            id: 'passcode_data',
            salt: bufferToBase64(salt),
            created: new Date().toISOString(),
          });

          request.onsuccess = () => {
            setEncryptionKey(key);
            setIsAuthenticated(true);
            setHasPasscode(true);
            resolve(true);
          };

          request.onerror = () => reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error creating passcode:', error);
      throw error;
    }
  };

  const unlock = async (passcode) => {
    try {
      const db = await indexedDB.open('AravelaDB', 1);
      
      return new Promise(async (resolve, reject) => {
        db.onsuccess = async () => {
          const transaction = db.result.transaction([STORES.AUTH], 'readonly');
          const store = transaction.objectStore(STORES.AUTH);
          const request = store.get('passcode_data');

          request.onsuccess = async () => {
            if (!request.result) {
              reject(new Error('No passcode set'));
              return;
            }

            try {
              const salt = base64ToBuffer(request.result.salt);
              const key = await deriveKey(passcode, salt);

              // Verify by attempting to decrypt a test entry
              // If user has diary entries, try to decrypt one
              // If not, just accept the key
              setEncryptionKey(key);
              setIsAuthenticated(true);
              resolve(true);
            } catch (error) {
              reject(new Error('Invalid passcode'));
            }
          };

          request.onerror = () => reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error unlocking:', error);
      throw error;
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setEncryptionKey(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        encryptionKey,
        isLoading,
        hasPasscode,
        createPasscode,
        unlock,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};